/* eslint-disable lit-a11y/click-events-have-key-events */
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ImageTypeNameEnum, ItemTypeEnum } from '@inter-ikea-kompis/enums';
import type { MeasurementSystemEnum } from '@inter-ikea-kompis/enums';
import type {
	ILocalizedInformation,
	IProduct,
	IShoppingProduct,
	IStore,
	IStoreAvailability,
	ITheme,
	IZipAvailability
} from '@inter-ikea-kompis/types';
import {
	AvailabilityUtility,
	DebounceManager,
	DexfSettingsUtility,
	PriceUtility,
	ProductDescriptionProductMeasureEnum,
	ProductDescriptionUtility,
	ProductUtility,
	ZipAvailabilityUtility
} from '@inter-ikea-kompis/utilities';
import type { IKompisElementPropertyValue, RenderResult } from '@inter-ikea-kompis/web-component';

import { FunctionIcon, FunctionIconEnum } from '@inter-ikea-kompis/component-function-icon';
import { Image, ImageAspectRatioEnum } from '@inter-ikea-kompis/component-image';
import {
	PriceModule,
	PriceModuleProductLinkEnum,
	PriceModuleUtility
} from '@inter-ikea-kompis/component-price-module';

import type { ButtonVariant } from '@ingka/button-webc';
import {
	Button as SkapaButton,
	IconButton as SkapaIconButton
} from '@ingka/button-webc/passive.js';
import { Text } from '@ingka/common-styles-webc';
import { checkmark, informationCircle, registerIcon } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import type { StatusColour } from '@ingka/status-webc';
import { Status as SkapaStatus } from '@ingka/status-webc/passive.js';
import { Tooltip as SkapaTooltip } from '@ingka/tooltip-webc/passive.js';

import ListGalleryCardMediaEnum from '../../enums/ListGalleryCardMediaEnum.js';
import ProductCardInfoIconButtonLinkTargetEnum from '../../enums/ProductCardInfoIconButtonLinkTargetEnum.js';
import ProductCardLayoutEnum from '../../enums/ProductCardLayoutEnum.js';
import type ProductCardModalEnum from '../../enums/ProductCardModalEnum.js';
import ProductCardPriceLayoutTypeEnum from '../../enums/ProductCardPriceLayoutTypeEnum.js';
import ProductCardSelectBehaviourEnum from '../../enums/ProductCardSelectBehaviourEnum.js';
import ProductCardSelectedStateEnum from '../../enums/ProductCardSelectedStateEnum.js';
import type IProductCardDexfSettings from '../../types/IProductCardDexfSettings.js';
import type IProductCardFunctionIcon from '../../types/IProductCardFunctionIcon.js';
import type IProductCardTranslations from '../../types/IProductCardTranslations.js';
import ProductCardMedia from '../product-card-media/ProductCardMedia.js';
import ProductCardVariants from '../product-card-variants/ProductCardVariants.js';
import ListGalleryCardStyle from './ListGalleryCardStyle.js';

/**
 * Product card list and gallery card component.
 *
 * @event modalClose Triggered when the modal should be closed.
 * @event modalOpen Triggered when a modal should be opened. "event.detail.visibleModal" tells you what modal that should be opened.
 * @event infoIconButtonClick Triggered when the info icon button is clicked. Only available when layout is set to "list".
 * @event productVariantClick Triggered when a product variant thumbnail is clicked.
 * @event productVariantShowMoreClick Triggered when the show more variants button is clicked.
 * @event select Triggered when the user clicks the card and selects it, or by pressing the Enter or the Space key after selecting it with tab.
 */

export default class ListGalleryCard extends KompisElement {
	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// The "main" product to show in the card. An object of type IProduct.
	@property({ type: Object, required: true })
	public product!: IProduct;

	// Defines the overall layout of the card.
	@property({ type: String })
	public layout: ProductCardLayoutEnum = ProductCardLayoutEnum.list;

	// Localized information. Deprecated - use "translations" and "settings" instead.
	@property({ type: Object })
	public localizedInformation: ILocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: IProductCardTranslations | null = null;

	// The settings retrieved from settings service.
	@property({ type: Object })
	public settings: IProductCardDexfSettings | null = null;

	// Optional array of function icons.
	@property({ type: Object })
	public functionIcons: IProductCardFunctionIcon[] | null = null;

	// The aspect ratio that the images should adopt.
	@property({ type: String })
	public imageAspectRatio?: ImageAspectRatioEnum = ImageAspectRatioEnum.ORIGINAL;

	// Defines the image sort order. When only one image is being displayed, the first image matching the type is used.
	@property({ type: Object })
	public imageSortOrder?: ImageTypeNameEnum[] | null = null;

	// Defines the image sort order for the image displayed on hover when "media" is set to "switchImageOnHover".
	@property({ type: Object })
	public hoverImageSortOrder?: ImageTypeNameEnum[] = [
		ImageTypeNameEnum.functionalProductPicture,
		ImageTypeNameEnum.contextProductPicture
	];

	// Disables links. This attribute should be set to "true" when in a Kiosk.
	@property({ type: Boolean })
	public disableLinks = false;

	// Info icon button link URL. Only available when layout is set to "list".
	@property({ type: String })
	public infoIconButtonLinkUrl: string | null = null;

	// Shows an info icon button. Only available when layout is set to "list".
	@property({ type: Boolean })
	public showInfoIconButton = false;

	// Info icon button link target. Only available when layout is set to "list".
	@property({ type: String })
	public infoIconButtonLinkTarget: ProductCardInfoIconButtonLinkTargetEnum =
		ProductCardInfoIconButtonLinkTargetEnum.self;

	// Optional array of type IProduct used for variations of a product when card should represent multiple variations, i.e for auto adapt products. Price of cheapest variation will be shown as "from price". Only available when layout is set to "list".
	@property({ type: Object })
	public productVariations: IProduct[] | null = null;

	// Price module layout type.
	@property({ type: String })
	public priceModuleLayout: ProductCardPriceLayoutTypeEnum = ProductCardPriceLayoutTypeEnum.default;

	// When enabled it is possible select the product card using keyboard or mouse. Set selected state with the "selectedState" attribute when using the "list" layout.
	@property({ type: String })
	public selectBehaviour: ProductCardSelectBehaviourEnum = ProductCardSelectBehaviourEnum.disabled;

	// Show a modal of type ProductCardModalEnum.
	@property({ type: String })
	public visibleModal: ProductCardModalEnum | null = null;

	// Product measures.
	@property({ type: String })
	public productMeasures: ProductDescriptionProductMeasureEnum =
		ProductDescriptionProductMeasureEnum.none;

	// Show text describing the color and/or material of the product.
	@property({ type: Boolean })
	public showDesignText = false;

	// Show product package weight info.
	@property({ type: Boolean })
	public showWeight = false;

	// Sets how media should be displayed.
	@property({ type: String })
	public media: ListGalleryCardMediaEnum = ListGalleryCardMediaEnum.singleImage;

	// Show price excl tax.
	@property({ type: Boolean })
	public showPriceExclTax = false;

	// Show product package measures.
	@property({ type: Boolean })
	public showPackageMeasures = false;

	// Selected state. Only available when layout is set to "list".
	@property({ type: String })
	public selectedState: ProductCardSelectedStateEnum = ProductCardSelectedStateEnum.unselected;

	// Optional array of type IProduct used for products included in the main product, i.e. child products. Needed for price to be calculated and displayed for an assembly product (ASP). Products included in all variations is needed when card should represent multiple variations of an assembly product.
	@property({ type: Object })
	public includedProducts: IProduct[] | null = null;

	// Zip availability for the product(s). Object or array. Array is used when card should represent multiple variations of a product (array with availability for all variations), an assembly product (ASP) (array of availabilty for all child items) or multiple variations of assembly products (array of availabilty for all child items of all variations).
	@property({ type: Object })
	public zipAvailability: IZipAvailability | IZipAvailability[] | null = null;

	// Store availability for the product(s). Object or array. Array is used when card should represent multiple variations of a product (array with availability for all variations), an assembly product (ASP) (array of availabilty for all child items) or multiple variations of assembly products (array of availabilty for all child items of all variations).
	@property({ type: Object })
	public storeAvailability: IStoreAvailability | IStoreAvailability[] | null = null;

	// Stores. Used for availability status indicator.
	@property({ type: Object })
	public stores: IStore[] | null = null;

	// Selected store id. Used for availability status indicator.
	@property({ type: String })
	public storeId: string | null = null;

	// Aria label that will be read up together with the product name and description. Only rendered when selectedState = checkbox. Tells the user that, when navigating to the product card, it has the "checkbox icon". What that means depends on how you have implemented it in your planner.
	@property({ type: String })
	public selectedStateAriaLabel: string | null = null;

	// Shows a label that tells the customer that there are more options available.
	@property({ type: Boolean })
	public showProductVariants = false;

	// Optional array of type IProduct which will be displayed as "More options available".
	@property({ type: Object })
	public productVariants: IProduct[] | null = null;

	// Number of product variant thumbnails to initially show. All product variant thumbnails can be expanded.
	@property({ type: Number })
	public productVariantsLimit: number | null = null;

	// Override market setting "useMetricMeasures" by controlling the measurement system in the planner.
	@property({ type: String })
	public measurementSystem: MeasurementSystemEnum | null = null;

	// Hides the text describing the type of the product. Can be turned on when e.g. listing the same product type in a list to avoid redundant information. Please note that it should be clear to the user what type of products are listed when this is turned on.
	@property({ type: Boolean })
	public hideTypeName?: boolean = false;

	private isHovered = false;
	private isPointerDown = false;
	private hasFocus = false;
	private priceModuleShoppingProducts: IShoppingProduct[] | null;

	private dexfSettings: IProductCardDexfSettings | null = null;

	private debounce = new DebounceManager();

	public override render(): RenderResult {
		// We want translations and settings to be required, but since all props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslationsAndSettings()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}

		this.priceModuleShoppingProducts = this.getPriceModuleShoppingProducts();

		if (!this.priceModuleShoppingProducts) {
			// If we fail to provide price module with shopping products (i.e. failing to show price when expected), we should not show the card at all.
			return null;
		}

		return html`${this.getCard()}`;
	}

	public override async connectedCallback(): Promise<void> {
		registerIcon(checkmark);
		registerIcon(informationCircle);

		this.customElements.define('skapa-status', SkapaStatus);
		this.customElements.define('skapa-button', SkapaButton);
		this.customElements.define('skapa-icon-button', SkapaIconButton);
		this.customElements.define('skapa-tooltip', SkapaTooltip);
		this.customElements.define('skapa-icon', SkapaIcon);
		this.customElements.define('kompis-price-module', PriceModule);
		this.customElements.define('kompis-function-icon', FunctionIcon);
		this.customElements.define('kompis-image', Image);
		this.customElements.define('kompis-product-card-media', ProductCardMedia);
		this.customElements.define('kompis-product-card-variants', ProductCardVariants);

		super.connectedCallback();
	}

	public override focus(): void {
		const actionElement: HTMLLinkElement | HTMLButtonElement | null =
			this.shadowRoot
				?.querySelector(this.customElements.getInternalName('kompis-price-module'))
				?.shadowRoot.querySelector('.actionElement') ?? null;

		actionElement?.focus();
	}

	protected override shouldUpdate(
		changedProperties: Map<string, IKompisElementPropertyValue>
	): boolean {
		if (
			changedProperties.has('localizedInformation') &&
			this.localizedInformation &&
			!this.settings
		) {
			this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
		}
		if (changedProperties.has('settings') && this.settings) {
			this.dexfSettings = this.settings;
		}

		return true;
	}

	private hasTranslationsAndSettings(): boolean {
		return !!this.localizedInformation || !!(this.translations && this.settings);
	}

	private getTranslations(): IProductCardTranslations {
		return this.translations
			? this.translations
			: <IProductCardTranslations>this.localizedInformation?.translations;
	}

	private getSettings(): IProductCardDexfSettings {
		return <IProductCardDexfSettings>this.dexfSettings;
	}

	private getCard(): RenderResult {
		return html`<style>
				${ListGalleryCardStyle.getStyle({
					theme: this.theme,
					layout: this.layout,
					selectedState: this.selectedState,
					hasFocus: this.hasFocus,
					hasZipAvailabilities: !!this.zipAvailability,
					selectBehaviour: this.selectBehaviour,
					hasProductActionElement: this.hasProductActionElement()
				})}
			</style>
			<div class="container">
				<div class="outline"></div>
				${this.getContainer()}
			</div>`;
	}
	private getContainer(): RenderResult {
		const content = [
			this.getPriceModuleWrapper(),
			this.getMedia(),
			this.getFunctionIcons(),
			this.getCheckboxWrapper(),
			this.getAvailabilityContent(),
			this.getProductVariants()
		];

		// eslint-disable-next-line lit-a11y/mouse-events-have-key-events
		return html`
			<div
				class="innerContainer"
				@click="${this.onClick.bind(this)}"
				@mouseover="${this.onMouseOver.bind(this)}"
				@mouseleave="${this.onMouseLeave.bind(this)}"
				@pointerdown="${this.onPointerDown.bind(this)}"
			>
				${content}
			</div>
		`;
	}

	private getAvailabilityContent(): RenderResult {
		return html`
			<div class="availabilityContent">
				${this.getZipAvailabilityStatusIndicator()}${this.getStoreAvailabilityStatusIndicator()}
			</div>
		`;
	}

	private getMedia(): RenderResult {
		return html`
			<kompis-product-card-media
				.theme="${this.theme}"
				.product="${this.product}"
				.localizedInformation="${this.localizedInformation}"
				.translations="${this.translations}"
				.media="${this.media}"
				.hovered="${this.isHovered}"
				.imageAspectRatio="${this.imageAspectRatio}"
				.imageSortOrder="${this.imageSortOrder}"
				.hoverImageSortOrder="${this.hoverImageSortOrder}"
			></kompis-product-card-media>
		`;
	}

	private getPriceModuleWrapper(): RenderResult {
		const infoIconButton = this.getInfoIconButton();
		const priceModule = this.getPriceModule();
		if (!priceModule && !infoIconButton) {
			return null;
		}

		return html`
			<div class="priceModuleWrapper">
				${priceModule}
				<div class="separator"></div>
				${infoIconButton}
			</div>
		`;
	}

	private getPriceModule(): RenderResult {
		if (!this.priceModuleShoppingProducts) {
			return null;
		}

		const priceForMultipleProducts =
			ProductUtility.shouldCalculatePriceFromChildProducts(this.product) ||
			this.priceModuleShoppingProducts.length > 1 ||
			this.priceModuleShoppingProducts[0].quantity > 1;

		return html`
			<kompis-price-module
				@modalOpen="${(event: CustomEvent) =>
					this.dispatchEvent(
						new CustomEvent('modalOpen', {
							bubbles: true,
							detail: {
								visibleModal: <ProductCardModalEnum>event.detail.visibleModal
							}
						})
					)}"
				@modalClose="${() =>
					this.dispatchEvent(
						new CustomEvent('modalClose', { bubbles: true, detail: { visibleModal: null } })
					)}"
				@productNameClick="${() => {
					const selectedState = this.getNewSelectState();

					this.debounce.set(
						'productNameClick',
						() => {
							this.dispatchEvent(
								new CustomEvent('select', {
									bubbles: true,
									detail: { selectedState }
								})
							);
						},
						1
					);
				}}"
				@blur="${this.onBlur.bind(this)}"
				@focus="${this.onFocus.bind(this)}"
				.disableFocusStyle="${true}"
				.shoppingProducts="${this.priceModuleShoppingProducts}"
				.localizedInformation="${this.localizedInformation}"
				.translations="${this.translations}"
				.settings="${this.settings}"
				.theme="${this.theme}"
				.layout="${this.priceModuleLayout}"
				.productName="${this.product.content?.name}"
				.isFromPrice="${this.isAutoAdapt()}"
				.productLink="${this.getProductLinkState()}"
				.visibleModal="${this.visibleModal}"
				.disableLinks="${this.disableLinks}"
				.showPriceUnit="${!priceForMultipleProducts}"
				.showEcoFee="${true}"
				.showPriceExclTax="${this.showPriceExclTax}"
				.showEnergyLabel="${!priceForMultipleProducts}"
				.showRepairabilityIndex="${!priceForMultipleProducts}"
				.underline="${this.isHovered}"
				.showLowestPreviousSalesPrice="${!priceForMultipleProducts}"
				.isCombinedProduct="${priceForMultipleProducts}"
				.showOfferMessage="${true}"
				disableHeadings
			>
				${this.getDescription()} ${this.getSelectedStateAriaLabel()}
			</kompis-price-module>
		`;
	}

	private getPriceModuleShoppingProducts(): IShoppingProduct[] | null {
		if (this.isAutoAdapt()) {
			// Return variation with lowest price or child products for variation with lowest price
			// If not possible to calculate price for any variation, null is returned
			const variationsShoppingProductGroups = this.getShoppingProductGroupsForVariations();
			return PriceUtility.getShoppingProductsWithLowestTotalRegularPrice(
				variationsShoppingProductGroups
			);
		}

		if (ProductUtility.shouldCalculatePriceFromChildProducts(this.product)) {
			// Return child products if it is possible to calculate price from them (i.e. we have price data for all),
			// otherwise null is returned
			const includedShoppingProducts = ProductUtility.getIncludedShoppingProducts({
				product: this.product,
				includedProducts: this.includedProducts ?? [],
				ignoreInvalidProducts: true
			});
			return includedShoppingProducts.length > 0 &&
				includedShoppingProducts.length === this.product.content?.child?.length
				? includedShoppingProducts
				: null;
		}

		// Return main product
		return [{ quantity: 1, product: this.product }];
	}

	/**
	 * Creates an array of arrays of shopping products, one array with shopping products for each variation.
	 *
	 * If variation is a product where price should be calculated from child products,
	 * variation array will contain shopping products for those child products. Unless price can not be
	 * calculated for the variation (e.g. One child product missing price), then array for variation is omitted.
	 *
	 * If variation is a product with price, variation array will contain a shopping product for the variation itself.
	 *
	 * @returns Array of arrays of shopping products, one array with shopping products for each variation.
	 */
	private getShoppingProductGroupsForVariations(): IShoppingProduct[][] {
		if (!this.productVariations) {
			return [];
		}

		return this.productVariations
			.map((variation) => {
				if (ProductUtility.shouldCalculatePriceFromChildProducts(variation)) {
					// Get shopping products for all child products of the variation
					const variationIncludedShoppingProducts = ProductUtility.getIncludedShoppingProducts({
						product: variation,
						includedProducts: this.includedProducts ?? []
					});

					return variationIncludedShoppingProducts.length > 0 &&
						variationIncludedShoppingProducts.length === variation.content?.child?.length
						? variationIncludedShoppingProducts
						: null;
				} else {
					return [{ quantity: 1, product: variation }];
				}
			})
			.filter((shoppingProducts): shoppingProducts is IShoppingProduct[] => !!shoppingProducts);
	}

	private getSelectedStateAriaLabel(): RenderResult | null {
		if (
			this.selectedStateAriaLabel &&
			this.selectedState === ProductCardSelectedStateEnum.checkbox &&
			this.selectBehaviour === ProductCardSelectBehaviourEnum.selectable
		) {
			return html`<span class="visuallyHidden">${this.selectedStateAriaLabel}</span>`;
		}
		return null;
	}

	private getProductLinkState(): PriceModuleProductLinkEnum {
		const itemTypesWithLink = [ItemTypeEnum.art, ItemTypeEnum.spr];
		const productItemType = this.product.content?.itemType;

		if (
			productItemType &&
			!itemTypesWithLink.includes(productItemType) &&
			this.selectBehaviour === ProductCardSelectBehaviourEnum.productInformationPageLink
		) {
			return PriceModuleProductLinkEnum.disabled;
		}

		if (
			this.selectBehaviour === ProductCardSelectBehaviourEnum.productInformationPageLink &&
			!this.disableLinks
		) {
			return PriceModuleProductLinkEnum.productInformationPage;
		} else if (this.selectBehaviour === ProductCardSelectBehaviourEnum.selectable) {
			return PriceModuleProductLinkEnum.eventOnly;
		}

		return PriceModuleProductLinkEnum.disabled;
	}

	private getDescription(): string {
		return ProductDescriptionUtility.getDescription({
			settings: this.getSettings(),
			translations: this.getTranslations(),
			product: this.product,
			include: {
				productMeasures: this.productMeasures,
				designText: this.showDesignText,
				packageMeasures: this.showPackageMeasures,
				weight: this.showWeight,
				measurementSystem: this.measurementSystem
			},
			exclude: {
				typeName: this.hideTypeName
			}
		});
	}

	private queryTriggerElement(): SkapaIconButton | null {
		return (
			this.shadowRoot
				?.querySelector(this.customElements.getInternalName('skapa-tooltip'))
				?.querySelector(this.customElements.getInternalName('skapa-icon-button')) ?? null
		);
	}

	private getInfoIconButton(): RenderResult {
		if (this.layout !== ProductCardLayoutEnum.list || !this.showInfoIconButton) {
			return null;
		}

		const tertiaryVariant: ButtonVariant = 'tertiary';

		const { informationViewMoreInformation } = this.getTranslations();

		if (this.disableLinks || !this.infoIconButtonLinkUrl) {
			return html`
				<div class="infoIconButton">
					<skapa-tooltip>
						<skapa-icon-button
							slot="custom-trigger"
							@click="${(event: MouseEvent) => {
								event.preventDefault();
								event.stopPropagation();
								this.dispatchEvent(
									new CustomEvent('infoIconButtonClick', {
										bubbles: true,
										detail: { product: this.product, triggerElement: this.queryTriggerElement() }
									})
								);
							}}"
							variant="${tertiaryVariant}"
							size="xsmall"
						>
							${informationViewMoreInformation}
							<skapa-icon slot="icon" icon="information-circle"></skapa-icon>
						</skapa-icon-button>
						${informationViewMoreInformation}
					</skapa-tooltip>
				</div>
			`;
		}

		return html`
			<skapa-tooltip>
				<skapa-icon-button
					slot="custom-trigger"
					tabindex="-1"
					@click="${(event: MouseEvent) => {
						event.stopPropagation();
						this.dispatchEvent(
							new CustomEvent('infoIconButtonClick', {
								bubbles: true,
								detail: { product: this.product }
							})
						);
					}}"
					class="infoIconButton"
					href="${this.infoIconButtonLinkUrl}"
					?new-window="${this.infoIconButtonLinkTarget ===
					ProductCardInfoIconButtonLinkTargetEnum.blank}"
					size="xsmall"
					variant="${tertiaryVariant}"
				>
					${informationViewMoreInformation}
					<skapa-icon slot="icon" icon="information-circle"></skapa-icon>
				</skapa-icon-button>
				${informationViewMoreInformation}
			</skapa-tooltip>
		`;
	}

	private getFunctionIcons(): RenderResult {
		if (!this.functionIcons) {
			return null;
		}

		return html`
			<div class="functionIcons">
				${this.functionIcons.map(
					(icon) => html`
						<kompis-function-icon
							.theme="${this.theme}"
							.icon="${icon.icon}"
							.size="${FunctionIconEnum.small}"
							.label="${icon.title}"
						></kompis-function-icon>
					`
				)}
			</div>
		`;
	}

	private getCheckboxWrapper(): RenderResult {
		return html` <div class="checkboxIconContainer">${this.getCheckboxIcon()}</div> `;
	}

	private getCheckboxIcon(): RenderResult {
		if (
			this.selectBehaviour !== ProductCardSelectBehaviourEnum.selectable ||
			this.selectedState !== ProductCardSelectedStateEnum.checkbox ||
			this.layout !== ProductCardLayoutEnum.list
		) {
			return null;
		}
		return html`
			<div class="checkboxIcon">
				<skapa-icon icon="checkmark"></skapa-icon>
			</div>
		`;
	}

	private getZipAvailabilityStatusIndicator(): RenderResult {
		if (!this.zipAvailability) {
			return null;
		}

		return this.getStatusIndicator(
			ZipAvailabilityUtility.getLabel({
				translations: this.getTranslations(),
				zipAvailability: this.zipAvailability
			}),
			ZipAvailabilityUtility.getColor({
				zipAvailability: this.zipAvailability
			})
		);
	}

	private getStatusIndicator(statusLabel: string, statusColor: StatusColour | null): RenderResult {
		if (!statusColor) {
			return null;
		}

		return html`
			<div class="zipAvailabilityStatusIndicator">
				<skapa-status small colour="${statusColor}" class="text--body-s"
					>${statusLabel}</skapa-status
				>
			</div>
		`;
	}

	private getStoreAvailabilityStatusIndicator(): RenderResult {
		const store = this.stores?.find((store) => store.id === this.storeId) || null;
		if (!this.storeAvailability || !this.stores || !store?.name) {
			return null;
		}

		const statusLabel = AvailabilityUtility.getStoreAvailabilityLabel({
			translations: this.getTranslations(),
			storeAvailability: this.storeAvailability
		})
			?.replace('[[store]]', store.name)
			.replace('{{store}}', store.name);
		const statusColor = AvailabilityUtility.getStoreAvailabilityStatusColor(this.storeAvailability);

		if (!statusLabel || !statusColor) {
			return null;
		}

		return html`
			<div class="storeAvailabilityStatusIndicator">
				<skapa-status small colour="${statusColor}" class="text--body-s"
					>${statusLabel}</skapa-status
				>
			</div>
		`;
	}

	private getProductVariants(): RenderResult {
		const isAutoAdapt = this.isAutoAdapt();
		if (!this.showProductVariants || isAutoAdapt) {
			return null;
		}

		const productVariantsHeader = (): RenderResult => {
			if (this.layout === ProductCardLayoutEnum.gallery && !this.productVariants) {
				return null;
			}

			return html`
				<h4 class="${Text.classnames({ body: { s: true } })}">
					${this.getTranslations().productCardMoreOptionsAvailable}
				</h4>
			`;
		};

		const showProductVariants = (): RenderResult => {
			if (!this.productVariants || !this.productVariants.length) {
				return null;
			}

			return html`
				<kompis-product-card-variants
					@variantClick="${(event: CustomEvent) => {
						this.dispatchEvent(
							new CustomEvent('productVariantClick', { bubbles: true, detail: event.detail })
						);
					}}"
					@showMoreClick="${() => {
						this.dispatchEvent(
							new CustomEvent('productVariantShowMoreClick', {
								bubbles: true,
								detail: { product: this.product }
							})
						);
					}}"
					.localizedInformation="${this.localizedInformation}"
					.translations="${this.translations}"
					.settings="${this.settings}"
					.theme="${this.theme}"
					.limit="${this.productVariantsLimit}"
					.products="${this.productVariants}"
				></kompis-product-card-variants>
			`;
		};

		return html`
			<div class="productVariants">${productVariantsHeader()} ${showProductVariants()}</div>
		`;
	}

	private isAutoAdapt(): boolean {
		return (
			this.layout === ProductCardLayoutEnum.list &&
			!!this.productVariations &&
			this.productVariations.length > 0
		);
	}

	private getNewSelectState(): ProductCardSelectedStateEnum | null {
		if (this.layout !== ProductCardLayoutEnum.list) {
			return null;
		}
		return this.selectedState === ProductCardSelectedStateEnum.unselected
			? ProductCardSelectedStateEnum.checkbox
			: ProductCardSelectedStateEnum.unselected;
	}

	private hasProductActionElement(): boolean {
		return PriceModuleUtility.showInteractiveProductName(
			this.getProductLinkState(),
			this.disableLinks
		);
	}

	private onClick(event: CustomEvent | KeyboardEvent): void {
		const productActionElement: HTMLElement | null =
			this.shadowRoot
				.querySelector(this.customElements.getInternalName('kompis-price-module'))
				?.shadowRoot.querySelector('.actionElement') ?? null;

		if (productActionElement !== event.target) {
			productActionElement?.click();
		}
	}

	private onMouseOver(): void {
		if (this.isHovered || !this.hasProductActionElement()) {
			return;
		}

		this.isHovered = true;
		this.requestUpdate();
	}

	private onMouseLeave(): void {
		if (!this.isHovered || !this.hasProductActionElement()) {
			return;
		}

		this.isHovered = false;
		this.requestUpdate();
	}

	private onPointerDown(): void {
		if (!this.hasProductActionElement()) {
			return;
		}
		this.isPointerDown = true;
		if (this.hasFocus) {
			this.hasFocus = false;
			this.requestUpdate();
		}
	}

	private onFocus(): void {
		if (this.isPointerDown) {
			return;
		}
		this.hasFocus = true;
		this.requestUpdate();
	}

	private onBlur(): void {
		this.hasFocus = false;
		this.isPointerDown = false;
		this.requestUpdate();
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'skapa-status': SkapaStatus;
		'skapa-button': SkapaButton;
		'skapa-icon-button': SkapaIconButton;
		'skapa-tooltip': SkapaTooltip;
		'skapa-icon': SkapaIcon;
		'kompis-list-gallery-card': ListGalleryCard;
	}
}
